import React from 'react';

import CardDialog from '~/components/CardDialog';
import type { InnerTabSingleProps } from '~/components/core/Layout/InnerTabs/InnerTabs';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import useOrganization from '~/components/OrganizationContext';
import type { CoverageConfig } from '~/components/Policy/GenericPolicy/CoveredEntity/types';
import type {
  StatReserveConfigurationRowWithSortableId,
  StatReserveConfigurationTableProps,
} from '~/components/SystemConfiguration/ClaimConfiguration/StatReserveConfiguration/StatReserveConfigurationTable';
import StatReserveConfigurationTable from '~/components/SystemConfiguration/ClaimConfiguration/StatReserveConfiguration/StatReserveConfigurationTable';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  StatReserveConfigurationResponse as DataContractsStatReserveConfigurationResponse,
  StatReserveConfigurationResponseStatReserveConfigurationBase,
  StatReserveConfigurationResponseStatSubReserve,
  StatReserveConfigurationResponseSubReservesConfig,
  StatReserveConfigurationSubRule as DataContractsStatReserveConfigurationSubRule,
  StatReserveConfigurationSubRuleStatSubReserve,
  StatReserveConfigurationSubRuleSubReservesConfig,
} from '~/server_shared/openapi/data-contracts';
import { getLobDescription } from '~/Utils/lobUtils';

export type SubReservesConfig =
  | StatReserveConfigurationResponseSubReservesConfig
  | StatReserveConfigurationSubRuleSubReservesConfig;

export type StatSubReserveConfig =
  | StatReserveConfigurationResponseStatSubReserve
  | StatReserveConfigurationSubRuleStatSubReserve;

export interface StatReserveConfigurationSubRule extends DataContractsStatReserveConfigurationSubRule {
  id: number;
  countries?: string[];
  coverage_indemnity_sub_reserves_config?: Record<string, SubReservesConfig>;
  expenses_sub_reserves_amounts?: Record<string, StatSubReserveConfig>;
  indemnity_sub_reserves_amounts?: Record<string, StatSubReserveConfig>;
}

export interface StatReserveConfiguration extends StatReserveConfigurationResponseStatReserveConfigurationBase {
  id?: number;
  coverage_indemnity_sub_reserves_config?: Record<string, SubReservesConfig>;
  expenses_sub_reserves_amounts?: Record<string, StatSubReserveConfig>;
  indemnity_sub_reserves_amounts?: Record<string, StatSubReserveConfig>;
  is_sub_rule?: boolean;
  countries?: string[];
  states?: string[];
  sub_organization_ids?: number[];
  sub_rules?: StatReserveConfigurationSubRule[];
  is_all_countries?: boolean;
}

export interface StatReserveConfigurationResponse extends DataContractsStatReserveConfigurationResponse {
  stat_reserve_configurations: StatReserveConfiguration[];
}

const StatReserveConfigurationPage = (): React.JSX.Element => {
  const { lobConfigurationsDict = {} } = useLobConfiguration();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationId, supportedClaimTypes } = useOrganization();
  const {
    isLoading,
    isError,
    reloadData,
    data: statReserveConfigurationsResponse,
  } = useDataFetcher<StatReserveConfigurationResponse>(
    `/api/v1/organizations/${organizationId}/stat_reserve_configuration`,
    {}
  );
  const {
    isLoading: coveragesLoading,
    isError: coveragesError,
    data: coveragesList,
  } = useDataFetcher<CoverageConfig[]>(`/sysconfig/api/v1/organizations/${organizationId}/coverages`);
  const statReserveConfigDict = statReserveConfigurationsResponse?.stat_reserve_configurations
    ? statReserveConfigurationsResponse?.stat_reserve_configurations?.reduce(
        (
          acc: Record<string, StatReserveConfigurationRowWithSortableId[]>,
          statReserveConfiguration: StatReserveConfiguration
        ) => {
          const { lob } = statReserveConfiguration;
          if (supportedClaimTypes.includes(lob)) {
            acc[lob] = acc[lob] ?? [];
            acc[lob].push({
              id: statReserveConfiguration.coverage_config_id,
              statReserveConfiguration,
            });
          }
          return acc;
        },
        {}
      )
    : {};

  const coveragesSubOrganizationIdDict = coveragesList
    ? coveragesList.reduce(
        (acc: Record<number, number[] | undefined>, coverage: CoverageConfig) => {
          acc[coverage.id] = coverage.is_org_level
            ? undefined
            : coverage?.sub_organizations?.map((subOrg) => subOrg.id) || [];
          return acc;
        },
        {} as Record<number, number[] | undefined>
      )
    : {};

  const disabledCoverageConfigIds = React.useMemo(
    () => (coveragesList ?? []).filter((coverage) => coverage.is_disabled).map((coverage) => coverage.id),
    [coveragesList]
  );

  const tabs: InnerTabSingleProps<StatReserveConfigurationTableProps>[] = supportedClaimTypes.map((lob: string) => {
    return {
      label: getLobDescription(lob, lobConfigurationsDict),
      url: lob,
      component: StatReserveConfigurationTable,
      props: {
        statReserveConfigurationSortableRows: statReserveConfigDict[lob],
        coveragesSubOrganizationIdDict,
        disabledCoverageConfigIds,
        reloadData,
        isLoading: isLoading || coveragesLoading,
        isError: isError || coveragesError,
      },
    };
  });

  return (
    <div className="m-20">
      <OperationsBreadcrumbs currentTab="Reserve Configuration" />
      <CardDialog
        title="Reserve Configuration"
        subheader="Configure custom rules to automatically set initial reserves when opening a new exposure."
      >
        <InnerTabs tabs={tabs} />
      </CardDialog>
    </div>
  );
};

export default StatReserveConfigurationPage;
